/*
 * Pool Busters — Service Status
 * ---------------------------------------------------------------------------
 * Tells customers whether OUTDOOR SERVICE APPOINTMENTS are running or
 * cancelled today. Completely standalone: it shares no code and no DOM with
 * the Pool Forecast section, and neither one can break the other.
 *
 *  - Vanilla JS, no libraries. Async, non-blocking. Multiple instances safe.
 *  - Weather comes from Open-Meteo — the same free, keyless service the
 *    forecast section already uses. No API key, no proxy, no account.
 *  - Always reads the STORE's coordinates (Fonthill), never the visitor's.
 *    Service status is about where the crews work.
 *  - The value evaluated is RAINFALL ACCUMULATION IN MILLIMETRES
 *    (rain + showers, falling back to total precipitation).
 *    Chance-of-rain percentage is never used to cancel service.
 *  - By default only rain falling inside SERVICE HOURS counts, so a downpour
 *    at 4am that has cleared by opening does not cancel the day. The hourly
 *    figures are used for that; the whole-day total is only a fallback.
 *  - Missing, failed or malformed weather NEVER produces a cancellation —
 *    it produces the neutral "check back shortly" state.
 *  - Cancellation rules live in one array, so more can be added later
 *    without touching the renderer.
 *  - Cached in sessionStorage under a key that includes the store's calendar
 *    date, so a new day is never answered with yesterday's result.
 *  - No cookies, no geolocation prompt, no tracking.
 */
(function () {
  'use strict';

  if (window.PBServiceStatus && window.PBServiceStatus.__loaded) { window.PBServiceStatus.scan(); return; }

  var STORE = { lat: 43.0501, lon: -79.2496 };   // Fonthill / Pelham, Niagara
  var THUNDER = [95, 96, 99];                     // WMO thunderstorm codes

  /* ----------------------------------------------------------------------- *
   * THE RULE TABLE — add a rule here and nothing else has to change.
   * Each returns true when it should CANCEL outdoor service. A rule whose
   * data is missing returns false: it abstains rather than guessing.
   * ----------------------------------------------------------------------- */
  var RULES = [
    { id: 'rain',  test: function (w, c) { return w.rainMm !== null && w.rainMm >= c.threshold; } },
    { id: 'storm', test: function (w)    { return w.code !== null && THUNDER.indexOf(w.code) !== -1; } },
    { id: 'alert', test: function (w)    { return w.alert === true; } },
    { id: 'wind',  test: function (w, c) { return c.wind > 0 && w.gustKph !== null && w.gustKph >= c.wind; } }
  ];

  /* --------------------------------- icons --------------------------------- */
  var ICONS = {
    running:   '<svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><circle cx="16" cy="16" r="12.5" stroke="currentColor" stroke-width="1.8"/><path d="M10.5 16.4l3.7 3.6 7.3-7.6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    cancelled: '<svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><path d="M9.5 19h13a5 5 0 0 0 .4-9.98A6.6 6.6 0 0 0 10.4 10.6 5.3 5.3 0 0 0 9.5 19Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><g stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M11.5 23l-1.4 3.6"/><path d="M17 23l-1.4 3.6"/><path d="M22.5 23l-1.4 3.6"/></g></svg>',
    custom:    '<svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><circle cx="16" cy="16" r="12.5" stroke="currentColor" stroke-width="1.8"/><path d="M16 10.2v.2" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/><path d="M16 14.4v7.4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    unknown:   '<svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><circle cx="16" cy="16" r="12.5" stroke="currentColor" stroke-width="1.8"/><path d="M16 9.4V16l4.4 2.8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  };

  /* ------------------------------ test weather ----------------------------- *
   * Theme-Editor preview only. Local data, never a network call.              */
  var MOCKS = {
    dry:        { code: 0,  rain: 0,   gust: 14, alert: false },
    drizzle:    { code: 51, rain: 1.4, gust: 16, alert: false },
    rain:       { code: 63, rain: 4.2, gust: 24, alert: false },
    heavy_rain: { code: 65, rain: 14,  gust: 34, alert: false },
    storm:      { code: 95, rain: 8,   gust: 52, alert: true  },
    windy:      { code: 2,  rain: 0,   gust: 78, alert: false },
    // Falls entirely before opening — proves overnight rain is ignored.
    overnight:  { code: 63, rain: 6,   gust: 22, alert: false, when: 'overnight' }
  };

  /* Build a day of hourly buckets for the test modes, dropping the rain either
   * inside service hours or in the small hours. */
  function mockWeather(m, cfg) {
    var wetHour = (m.when === 'overnight') ? 4 : Math.max(1, Math.floor((cfg.startMin + 90) / 60));
    var hours = [];
    for (var h = 1; h <= 24; h++) hours.push({ end: h * 60, mm: (h === wetHour) ? m.rain : 0 });
    return { dayRainMm: m.rain, hours: hours, code: m.code, gustKph: m.gust, alert: m.alert, updated: null };
  }

  /* -------------------------------- helpers -------------------------------- */
  function $(r, s) { return r.querySelector(s); }
  function setText(el, t) { if (el) el.textContent = t; }
  function show(el, on) { if (el) el.hidden = !on; }
  function num(v) { var n = Number(v); return (v === null || v === undefined || v === '' || isNaN(n)) ? null : n; }
  function flt(v, f) { var n = parseFloat(v); return isFinite(n) ? n : f; }

  /* "08:30" -> 510 minutes past local midnight. */
  function parseClock(v, fallback) {
    var m = /^\s*(\d{1,2}):(\d{2})\s*$/.exec(String(v || ''));
    if (!m) return fallback;
    var h = parseInt(m[1], 10), mi = parseInt(m[2], 10);
    if (!(h >= 0 && h <= 24) || !(mi >= 0 && mi < 60)) return fallback;
    return h * 60 + mi;
  }

  /* 510 -> "8:30 AM" */
  function fmtClock(mins) {
    var h = Math.floor(mins / 60), mi = mins % 60;
    var ap = h >= 12 ? 'PM' : 'AM';
    h = h % 12; if (h === 0) h = 12;
    return h + ':' + (mi < 10 ? '0' + mi : mi) + ' ' + ap;
  }

  /* The store's own calendar day, so the cache can never straddle midnight. */
  function storeDay(tz) {
    try {
      return new Intl.DateTimeFormat('en-CA', { timeZone: tz || 'America/Toronto', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    } catch (e) {
      var d = new Date();
      return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
    }
  }

  function fmtTime(s) {
    if (!s) return '';
    var m = /(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(s);
    if (!m) return '';
    var h = parseInt(m[4], 10), mi = m[5], ap = h >= 12 ? 'PM' : 'AM';
    h = h % 12; if (h === 0) h = 12;
    return h + ':' + mi + ' ' + ap;
  }

  function readCache(k, min) {
    try {
      var raw = window.sessionStorage.getItem(k);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (Date.now() - o._t > (min > 0 ? min : 45) * 60000) return null;
      return o.d;
    } catch (e) { return null; }
  }
  function writeCache(k, d) {
    try { window.sessionStorage.setItem(k, JSON.stringify({ _t: Date.now(), d: d })); } catch (e) {}
  }

  /* --------------------------------- fetch --------------------------------- */
  var inflight = {};

  function fetchJson(url, ms) {
    var controller = ('AbortController' in window) ? new AbortController() : null;
    var timer = setTimeout(function () { if (controller) controller.abort(); }, ms || 8000);
    return fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-store', signal: controller ? controller.signal : undefined })
      .then(function (r) { clearTimeout(timer); if (!r.ok) throw new Error('bad'); return r.json(); });
  }

  function normalize(d) {
    if (!d || !d.daily) throw new Error('shape');
    var day = d.daily, cur = d.current || {};
    var first = function (a) { return (a && a.length) ? a[0] : null; };
    var rain = num(first(day.rain_sum));
    var showers = num(first(day.showers_sum));
    var rainMm = (rain !== null || showers !== null) ? ((rain || 0) + (showers || 0)) : num(first(day.precipitation_sum));
    var gustNow = num(cur.wind_gusts_10m), gustDay = num(first(day.wind_gusts_10m_max));
    var gust = (gustNow === null && gustDay === null) ? null : Math.max(gustNow || 0, gustDay || 0);
    return {
      dayRainMm: rainMm,
      hours: hourlyBuckets(d),
      code: num(cur.weather_code) !== null ? num(cur.weather_code) : num(first(day.weather_code)),
      gustKph: gust,
      alert: false,                      // the keyless feed publishes no warnings
      updated: cur.time || null,
      source: 'open-meteo'
    };
  }

  /* Today's hourly rainfall as [{ end: minutes-past-midnight, mm }].
   * Open-Meteo hourly accumulations are the sum of the PRECEDING hour, so the
   * bucket stamped 09:00 covers 08:00–09:00. Returns null if unusable. */
  function hourlyBuckets(d) {
    var h = d && d.hourly;
    if (!h || !h.time || !h.time.length) return null;
    var out = [];
    for (var i = 0; i < h.time.length; i++) {
      var m = /T(\d{2}):(\d{2})/.exec(String(h.time[i]));
      if (!m) continue;
      var r = num(h.rain && h.rain[i]);
      var sh = num(h.showers && h.showers[i]);
      var mm = (r !== null || sh !== null) ? ((r || 0) + (sh || 0)) : num(h.precipitation && h.precipitation[i]);
      if (mm === null) continue;
      out.push({ end: parseInt(m[1], 10) * 60 + parseInt(m[2], 10), mm: mm });
    }
    return out.length ? out : null;
  }

  /* Rainfall inside [startMin, endMin] of the local day. A bucket that only
   * partly overlaps the window contributes its overlapping share, so rain at
   * 8:00–8:30 counts for half of that hour against an 8:30 start. */
  function windowRain(hours, startMin, endMin) {
    if (!hours || !hours.length || !(endMin > startMin)) return null;
    var total = 0;
    for (var i = 0; i < hours.length; i++) {
      var bEnd = hours[i].end, bStart = bEnd - 60;
      var overlap = Math.min(bEnd, endMin) - Math.max(bStart, startMin);
      if (overlap > 0) total += hours[i].mm * (overlap / 60);
    }
    return Math.round(total * 100) / 100;
  }

  /* The rainfall figure the rules actually judge on.
   *   mm         — the number, or null when it cannot be known
   *   scope      — 'window' (service hours) or 'day'
   *   safeUnder  — no figure, but the whole day is already under the
   *                threshold, so the window must be too: safe to say running */
  function serviceRain(w, cfg) {
    var day = num(w.dayRainMm);
    if (!cfg.hoursOnly) return { mm: day, scope: 'day', safeUnder: false };
    var win = windowRain(w.hours, cfg.startMin, cfg.endMin);
    if (win !== null) return { mm: win, scope: 'window', safeUnder: false };
    return { mm: null, scope: 'window', safeUnder: (day !== null && day < cfg.threshold) };
  }

  function getWeather(cfg) {
    var key = 'pbss2:' + storeDay(cfg.tz) + ':' + (Math.round(cfg.lat * 100) / 100) + ',' + (Math.round(cfg.lon * 100) / 100);
    var cached = readCache(key, cfg.cacheMin);
    if (cached) return Promise.resolve(cached);
    if (inflight[key]) return inflight[key];

    var url = 'https://api.open-meteo.com/v1/forecast?latitude=' + cfg.lat + '&longitude=' + cfg.lon +
      '&current=weather_code,wind_gusts_10m' +
      '&hourly=rain,showers,precipitation' +
      '&daily=weather_code,precipitation_sum,rain_sum,showers_sum,wind_gusts_10m_max' +
      '&timezone=auto&wind_speed_unit=kmh&forecast_days=1';

    var p = fetchJson(url, 8000)
      .then(function (d) { var n = normalize(d); delete inflight[key]; writeCache(key, n); return n; })
      .catch(function () { delete inflight[key]; return null; });
    inflight[key] = p;
    return p;
  }

  /* -------------------------------- config --------------------------------- */
  function readConfig(root) {
    var d = root.dataset || {};
    var copy = {}, rules = {};
    var el = root.querySelector('[data-pbss-copy]');
    if (el) {
      try { copy = JSON.parse(el.textContent || '{}') || {}; rules = copy.rules || {}; }
      catch (e) { copy = {}; rules = {}; }
    }
    return {
      mode: d.pbssMode || 'auto',
      lat: flt(d.pbssLat, STORE.lat),
      lon: flt(d.pbssLon, STORE.lon),
      tz: d.pbssTz || 'America/Toronto',
      cacheMin: Math.max(5, parseInt(d.pbssCacheMin, 10) || 45),
      threshold: Math.max(0, flt(d.pbssThreshold, 2)),
      hoursOnly: d.pbssHoursOnly !== 'false',
      startMin: parseClock(d.pbssStart, 510),   // 08:30
      endMin: parseClock(d.pbssEnd, 1020),      // 17:00
      wind: Math.max(0, flt(d.pbssWind, 0)),
      showRain: d.pbssShowRain === 'true',
      showUpdated: d.pbssShowUpdated === 'true',
      animate: d.pbssAnimate === 'true',
      retail: d.pbssRetail || 'cancelled',
      testMode: d.pbssTest || 'off',
      copy: copy,
      rules: rules
    };
  }

  /* ------------------------------- evaluate -------------------------------- *
   * Pure function: weather in, state out. Easy to reason about, easy to test.  */
  function evaluate(w, cfg) {
    var rain = serviceRain(w, cfg);
    // The rules judge on the service-hours figure, not the calendar-day total.
    w = { rainMm: rain.mm, code: w.code, gustKph: w.gustKph, alert: w.alert };

    var reasons = [];
    for (var i = 0; i < RULES.length; i++) {
      var rule = RULES[i];
      // Rainfall is on unless explicitly switched off; the others are opt-in.
      var on = (rule.id === 'rain') ? (cfg.rules.rain !== false) : (cfg.rules[rule.id] === true);
      if (!on) continue;
      var hit = false;
      try { hit = !!rule.test(w, cfg); } catch (e) { hit = false; }
      if (hit) reasons.push(rule.id);
    }

    var rainLine = null;
    if (rain.mm !== null && cfg.showRain) {
      var when = (rain.scope === 'window')
        ? fmtClock(cfg.startMin) + '–' + fmtClock(cfg.endMin)
        : 'today';
      rainLine = 'Forecast rainfall ' + when + ': ' + (Math.round(rain.mm * 10) / 10).toFixed(1) +
                 ' mm · cancellation threshold ' + cfg.threshold.toFixed(1) + ' mm';
    }

    // Nothing usable to judge on, and no other rule fired → stay neutral.
    if (rain.mm === null && !rain.safeUnder && reasons.length === 0) {
      return { state: 'unknown', rainLine: null, reasons: [] };
    }

    return { state: reasons.length ? 'cancelled' : 'running', rainLine: rainLine, reasons: reasons };
  }

  /* -------------------------------- render --------------------------------- */
  function apply(root, cfg, state, rainLine, updated) {
    var copy = cfg.copy[state] || {};

    root.classList.remove('pbss--running', 'pbss--cancelled', 'pbss--unknown', 'pbss--custom');
    root.classList.add('pbss--' + state);
    if (cfg.animate && state === 'cancelled') root.classList.add('pbss--animate');
    else root.classList.remove('pbss--animate');

    var icon = $(root, '[data-pbss-icon]');
    if (icon) icon.innerHTML = ICONS[state] || ICONS.unknown;

    setText($(root, '[data-pbss-eyebrow]'), copy.eyebrow || 'Service Status');
    setText($(root, '[data-pbss-headline]'), copy.headline || '');

    var det = $(root, '[data-pbss-detail]');
    if (det) { det.textContent = copy.detail || ''; show(det, !!copy.detail); }

    var rn = $(root, '[data-pbss-rain]');
    if (rn) {
      var wantRain = !!(cfg.showRain && rainLine && (state === 'running' || state === 'cancelled'));
      rn.textContent = wantRain ? rainLine : '';
      show(rn, wantRain);
    }

    var rt = $(root, '[data-pbss-retail]');
    if (rt) {
      var text = cfg.copy.retail || '';
      var wantRetail = !!text && (cfg.retail === 'always' ||
                        (cfg.retail === 'cancelled' && (state === 'cancelled' || state === 'custom')));
      rt.textContent = text;
      show(rt, wantRetail);
    }

    var up = $(root, '[data-pbss-updated]');
    if (up) {
      var t = cfg.showUpdated ? fmtTime(updated) : '';
      up.textContent = t ? ('Checked ' + t) : '';
      show(up, !!t);
    }

    var live = $(root, '[data-pbss-live]');
    if (live) live.textContent = (copy.eyebrow || 'Service status') + ': ' + (copy.headline || '');
  }

  /* -------------------------------- refresh -------------------------------- */
  function refresh(root, cfg) {
    var p;
    try {
      if (cfg.testMode && cfg.testMode !== 'off' && MOCKS[cfg.testMode]) {
        var m = MOCKS[cfg.testMode];
        p = Promise.resolve(mockWeather(m, cfg));
      } else {
        p = getWeather(cfg);
      }
    } catch (e) { p = Promise.resolve(null); }

    p.then(function (w) {
      try {
        if (!w) { apply(root, cfg, 'unknown'); return; }
        var v = evaluate(w, cfg);
        apply(root, cfg, v.state, v.rainLine, w.updated);
      } catch (e) {
        try { apply(root, cfg, 'unknown'); } catch (e2) {}
      }
    }, function () {
      try { apply(root, cfg, 'unknown'); } catch (e) {}
    });
  }

  /* ---------------------------------- init --------------------------------- */
  function initRoot(root) {
    if (!root || root.getAttribute('data-pbss-init') === '1') return;
    root.setAttribute('data-pbss-init', '1');

    var cfg;
    try { cfg = readConfig(root); } catch (e) { return; }

    // Manual override: no weather call at all. Already rendered by the server,
    // so this works even with JavaScript switched off.
    if (cfg.mode === 'running' || cfg.mode === 'cancelled' || cfg.mode === 'custom') {
      try { apply(root, cfg, cfg.mode); } catch (e) {}
      return;
    }

    refresh(root, cfg);

    // Re-check on a slow timer and whenever the tab comes back into view, so a
    // page left open overnight recalculates for the new day. Both read the
    // cache first, so this costs at most one request per cache window.
    try {
      var every = Math.max(15, cfg.cacheMin) * 60000;
      setInterval(function () { refresh(root, cfg); }, every);
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') refresh(root, cfg);
      });
    } catch (e) {}
  }

  function scan() {
    var r = document.querySelectorAll('.pbss');
    for (var i = 0; i < r.length; i++) { try { initRoot(r[i]); } catch (e) {} }
  }

  window.PBServiceStatus = {
    __loaded: true, scan: scan, initRoot: initRoot,
    evaluate: evaluate, normalize: normalize, rules: RULES, storeDay: storeDay,
    windowRain: windowRain, serviceRain: serviceRain
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan); else scan();
  document.addEventListener('shopify:section:load', function (e) {
    var c = e.target; if (!c) { scan(); return; }
    var r = c.querySelectorAll ? c.querySelectorAll('.pbss') : [];
    for (var i = 0; i < r.length; i++) { try { initRoot(r[i]); } catch (err) {} }
    if (c.classList && c.classList.contains('pbss')) { try { initRoot(c); } catch (err) {} }
  });
})();
